import type { z } from 'zod';
import { pageSchema, type Page } from '@teentack/shared';
import { apiFetch } from './api-client';

/**
 * Typed access to the paginated list endpoints.
 *
 * Every response is parsed with the shared schema before a component ever sees
 * it, so a contract change surfaces here rather than as `undefined` three
 * components deep.
 */
export type QueryValue = string | number | boolean | string[] | undefined;

export async function fetchPage<T extends z.ZodType>(
  path: string,
  item: T,
  query: Record<string, QueryValue> = {},
): Promise<Page<z.infer<T>>> {
  const result = await apiFetch<unknown>(`${path}${toSearch(query)}`);
  return pageSchema(item).parse(result) as Page<z.infer<T>>;
}

/**
 * A query object as a query string.
 *
 * Three rules the endpoints depend on:
 *
 * - An array repeats its key, which is the form Express parses back into an
 *   array. Joining on commas also works server-side, but repeating is what a
 *   reader of the URL expects.
 * - `false` is omitted rather than sent. Some list endpoints coerce flags with
 *   `Boolean(value)`, and the string "false" is truthy — so sending it would
 *   turn a flag on. Omitting means "off" everywhere.
 * - An empty string is omitted too, so a cleared filter input reads as no
 *   filter rather than as a search for nothing.
 */
function toSearch(query: Record<string, QueryValue>): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '' || value === false) continue;

    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry !== '') params.append(key, entry);
      }
      continue;
    }

    params.set(key, String(value));
  }

  const search = params.toString();
  return search ? `?${search}` : '';
}
